/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/prefer-default-export */

import { useCallback, useEffect, useState } from 'react';

import { getSavedFiltersAdapter } from '../../../adapters/weekly-report/filters-adapter';
import { tooltipLabels } from '../utils/consts';
import { isoFromDate, todayIso } from '../utils/date';
/**
 * @typedef positionLine
 * @type {Object}
 * @property {string} label
 */

/**
 * @typedef Filters
 * @property {number[] } contratosIds
 * @property {boolean} partesVisualizar
 * @property {number[]} idsEquipos
 * @property {number[]} idsEventos
 * @property {number[]} idsProveedores
 * @property {string} fechaDesde
 * @property {string} fechaHasta
 * @property {positionLine[]} posicionesLineas
 * @property {number | null} tipoUbicacionId
 * @property {number[]} ubicacionesIds
 * @property {number[]} serviciosIds
 * @property {number | null} partesId
 * @property {number[]} numerosSolicitudes
 * @property {number[]} eventosPozosIds
 * @property {number | null} estadoSolicitud
 * @property {number[]} centrosLogisticos
 * @property {number | null} tipoImputacion
 * @property {number[]} objetosImputacionesIds
 */

/** @type {Filters} */
export const initialFilters = {
  partesVisualizar: true,
  fechaDesde: isoFromDate,
  fechaHasta: todayIso,
  idsProveedores: [],
  contratosIds: [],
  idsEquipos: [],
  idsEventos: [],
  posicionesLineas: [],
  tipoUbicacionId: null,
  ubicacionesIds: [],
  serviciosIds: [],
  estadoParteId: null,
  numerosSolicitudes: [],
  eventosPozosIds: [],
  estadoSolicitud: null,
  centrosLogisticos: [],
  tipoImputacion: null,
  objetosImputacionesIds: []
};

const EXTENDED_VIEW_FILTER_KEY = 'extendedViewFilters';

export const useFilters = (isInspector) => {
  const sessionFilters = sessionStorage.getItem(EXTENDED_VIEW_FILTER_KEY);

  const [filters, setFilters] = useState(initialFilters);

  const [hasSessionFilters, setHasSessionFilters] = useState(!!sessionFilters);

  useEffect(() => {
    const savedFilters = async () => {
      // Load first session saved filters

      if (sessionFilters) {
        setFilters(JSON.parse(sessionFilters));
        setHasSessionFilters(true);

        return;
      }
      const userFilters = (await getSavedFiltersAdapter(isInspector)) ?? initialFilters;

      setFilters(userFilters);
    };

    savedFilters();
  }, []);

  const updateFilters = useCallback(
    /** @param {Filters} newFilters  */
    (newFilters) => {
      sessionStorage.setItem(EXTENDED_VIEW_FILTER_KEY, JSON.stringify(newFilters));
      setFilters(newFilters);
    },
    []
  );

  const cleanFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const handleArrayFilter = (filterKey, filterValue, index, arr) => {
    if (!filterValue.length) return;

    filterValue.forEach((v, idx) => {
      if (filterKey === 'posicionesLineas') {
        arr.push({
          id: `${index}-${idx}`,
          filter: v?.label,
          key: filterKey,
          tooltip: `${tooltipLabels[filterKey]}${v?.label}`
        });
      } else {
        arr.push({
          id: `${index}-${idx}`,
          filter: v?.nombre,
          key: filterKey,
          tooltip: `${tooltipLabels[filterKey]}${v?.label}`
        });
      }
    });
  };

  const handleObjectFilter = (filterKey, filterValue, index, arr) => {
    arr.push({
      id: index,
      filter: filterValue?.abreviatura || filterValue?.nombre,
      key: filterKey,
      tooltip: `${tooltipLabels[filterKey]}${filterValue?.nombre}`
    });
  };

  const handleDateFilter = (filterKey, filterValue, index, arr) => {
    const [date] = filterValue.split('T');
    const [year, month, day] = date.split('-');
    const formattedDate = [day, month, year].join('/');

    arr.push({
      id: index,
      filter: `${filterKey === 'fechaDesde' ? 'Desde' : 'Hasta'}: ${formattedDate}`,
      key: filterKey,
      tooltip: `${tooltipLabels[filterKey]}${formattedDate}`
    });
  };

  const handleBooleanFilter = (filterKey, filterValue, index, arr) => {
    arr.push({
      id: index,
      filter: filterValue ? 'Todos' : 'Partes Notificados',
      value: filterValue,
      key: filterKey,
      tooltip: `${tooltipLabels[filterKey]}${filterValue ? 'Todos' : 'Partes Notificados'}`
    });
  };

  const handleChips = () => {
    const arr = [];

    Object.entries(filters).forEach(([filterKey, filterValue], index) => {
      if (filterValue === null || filterValue === undefined) return;

      if (Array.isArray(filterValue)) {
        handleArrayFilter(filterKey, filterValue, index, arr);
      } else if (typeof filterValue === 'object') {
        handleObjectFilter(filterKey, filterValue, index, arr);
      } else if (filterKey === 'partesVisualizar') {
        handleBooleanFilter(filterKey, filterValue, index, arr);
      } else if ((filterKey === 'fechaDesde' || filterKey === 'fechaHasta') && filterValue !== '') {
        handleDateFilter(filterKey, filterValue, index, arr);
      } else {
        arr.push({
          id: index,
          filter: filterValue,
          key: filterKey,
          tooltip: `${tooltipLabels[filterKey]}${filterValue}`
        });
      }
    });

    return arr;
  };

  const handleDeleteChips = (filterObj) => {
    let newValue;
    let newFilterObject;

    if (filterObj.key === 'partesVisualizar') {
      newValue = !filterObj?.value;
    }
    if (Array.isArray(filters[filterObj.key])) {
      newValue = filters[filterObj.key].filter((obj) => obj.label !== filterObj.filter);
    } else if (typeof filters[filterObj.key] === 'object') {
      newValue = null;
    } else if (typeof filters[filterObj.key] === 'string') {
      newValue = '';
    }

    if (filterObj.key === 'contratosIds') {
      newFilterObject = { contratosIds: [], posicionesLineas: [] };
    }
    if (filterObj.key === 'tipoUbicacionId') {
      newFilterObject = { unidadesNegocioIds: [], operacionesIds: [], ubicacionesIds: [] };
    }
    const newFilter = {
      ...filters,
      ...newFilterObject,
      [filterObj.key]: newValue
    };

    updateFilters(newFilter);
  };

  useEffect(() => {}, [filters]);

  return {
    cleanFilters,
    filters,
    hasSessionFilters,
    initialFilters,
    updateFilters,
    handleChips,
    handleDeleteChips
  };
};
